import React from 'react';
import { Route } from 'react-router-dom';

import { lazy } from '@components/common/routeLoading';
import { Path } from '@components/routes/constants';
import { SECTION } from '@hooks/useDocumentTitle/constants';
import { definePageContextRoute, subjectResource } from '@context/pageContext';
import { RedirectRoute } from '@components/routes/redirect';

const TrackingPlans = lazy(
  () =>
    import(
      /* webpackChunkName: "trackingPlans.trackingPlanList" */ '@components/trackingPlans/trackingPlanList'
    ),
);
const TrackingPlanLayout = lazy(() =>
  import(/* webpackChunkName: "trackingPlans" */ './routesLayout').then((module) => ({
    default: module.TrackingPlanLayout,
  })),
);

const TrackingPlansDetails = lazy(
  () =>
    import(
      /* webpackChunkName: "trackingPlans.trackingPlanView" */ '@components/trackingPlans/trackingPlanView'
    ),
);

/**
 * See the note in @components/botManagement/routes on why these are definitions.
 *
 * The detail route keeps its `:id/*` splat: the tracking plan view nests a
 * further <Routes> block of its own (trackingPlanV2View), which is not converted
 * here. Its sub-pages therefore inherit the detail route's metadata rather than
 * declaring their own.
 *
 * `/trackingPlans/setup` and `/trackingPlans/setup/:mode` are NOT children here —
 * they are separate top-level routes in @components/routes/authorizedRoutes, and
 * rank above this route's `:id` param, so a plan can never be called "setup".
 */
export const trackingPlansRoutes = (
  <Route
    key={Path.TRACKING_PLANS}
    path={Path.TRACKING_PLANS}
    handle={{
      section: SECTION.trackingPlans,
      pageContext: definePageContextRoute({
        type: 'trackingPlan.list',
        title: 'Tracking plans',
        iconKey: 'govern',
      }),
    }}
  >
    <Route index element={<TrackingPlans />} />
    {/* `:id` is a layout route so the plan name is published once for the whole
        subtree, including the pages inside TrackingPlansDetails' own <Routes>.

        The `index` child is load-bearing, not decoration. React Router flattens a
        pathed layout route into a match candidate of its own and ranks it ABOVE its
        splat child (`/trackingPlans/:id` scores 17, `/trackingPlans/:id/*` scores 16 —
        splats carry a -2 penalty). Without an index route the bare detail URL
        therefore stops at this layout, whose <Outlet /> has nothing to render, and
        the page goes blank with no error and no error boundary. Every other converted
        section declares one; see @components/profiles/routes. */}
    <Route
      path=":id"
      element={<TrackingPlanLayout />}
      handle={{
        pageContext: definePageContextRoute({
          type: 'trackingPlan.details',
          title: 'Tracking plan details',
          iconKey: 'govern',
          resources: [subjectResource({ routeParam: 'id', type: 'trackingPlan' })],
        }),
      }}
    >
      <Route index element={<TrackingPlansDetails />} />
      <Route path="*" element={<TrackingPlansDetails />} />
    </Route>
    <Route path="*" element={<RedirectRoute />} />
  </Route>
);
